/**
 * FlowInputsDialog.tsx
 *
 * Collects user-provided input values for a system flow before execution.
 * Renders a form field for each flow input, pre-fills defaults, validates required fields.
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Label } from '@/components/ui/Label';
import { Settings2 } from 'lucide-react';

export interface FlowInputDefinition {
  name: string;
  type: 'string' | 'number' | 'boolean' | 'object' | 'array';
  required: boolean;
  default?: any;
  description?: string;
  label?: string;
  maxLength?: number;
  /**
   * Render this field masked (type=password) and treat it as a secret. Set by
   * flow inputs flagged `secret` on the system flow doc (e.g. a calling-card
   * PIN). The name-suffix heuristic below is a backstop for the common cases.
   */
  secret?: boolean;
  /**
   * Hide from the Quick Tests parameter dialog. The input still exists on the
   * flow (and its default — e.g. a `${env.X}` preset — is resolved server-side
   * at execution), it's just not offered as a user-editable knob. Used for
   * platform-managed inputs like the LibreSpeed `server_host` / `token`, which
   * always point at our own server.
   */
  hidden?: boolean;
  /**
   * Render as a multi-line textarea instead of a single-line input. For inputs
   * that carry prose rather than a value — e.g. the voice agent's per-run
   * "Additional AI Instructions" addendum. Ignored for secret fields, which
   * stay masked single-line.
   */
  multiline?: boolean;
}

export interface FlowInputsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  testLabel: string;
  inputs: FlowInputDefinition[];
  onSubmit: (values: Record<string, any>) => void;
  /** Key for persisting input values in localStorage (e.g. flow ID) */
  storageKey?: string;
}

const INPUTS_STORAGE_PREFIX = 'simcheck.quickTests.inputs';

// A default like `'${env.MQTT_PRESET_HOST}'` is a server-side preset
// reference — Firebase Functions resolves it at runtime via process.env.
// In the UI we treat presets as "leave blank to use the SimCheck default":
// pre-fill is skipped, the placeholder reads "SimCheck preset", and the
// value is omitted from the submit payload so the runtime's `resolveEnvDefault`
// fires (rather than echoing the literal template back as the input value).
const PRESET_TEMPLATE_RE = /\$\{env\.\w+\}/;
const isPresetTemplate = (v: any): boolean =>
  typeof v === 'string' && PRESET_TEMPLATE_RE.test(v);
const isSecretField = (input: { name: string; secret?: boolean }): boolean =>
  input.secret === true || /(?:password|secret|token|key|pin|passcode)$/i.test(input.name);

// The `apn` input is an OVERRIDE of the eSIM's configured APN (issue #161):
// when left empty it must be OMITTED from the payload — never filled from the
// flow's declared default — so the backend resolves profile.apn → group.apn →
// network.defaultApn at submit/dispatch. Baking the default ('internet') in
// here would register as an explicit override and defeat that resolution.
// The flow's default still applies server-side (ExecutionContext) when the
// eSIM has nothing configured, so behavior without any configured APN is
// unchanged. Exported so ExecutionDialog can default-open its Parameters
// section when the flow takes one of these.
export const ESIM_RESOLVED_INPUTS = new Set(['apn']);

/**
 * Build the final input payload from collected values: omit empty optional
 * fields; fall back to plain defaults (except eSIM-resolved inputs like
 * `apn` — see above); OMIT empty preset-template (`${env.X}`) inputs so the
 * server resolves them. Shared by the Quick Tests dialog and the /tests
 * Parameters section so both behave identically. See the long comment in
 * handleSubmit for the why behind preset omission.
 */
export function finalizeFlowInputs(
  inputs: FlowInputDefinition[],
  values: Record<string, any>,
): Record<string, any> {
  const out: Record<string, any> = {};
  for (const input of inputs) {
    const val = values[input.name];
    if (val !== undefined && val !== '') {
      out[input.name] = val;
    } else if (
      input.default !== undefined &&
      !isPresetTemplate(input.default) &&
      !ESIM_RESOLVED_INPUTS.has(input.name)
    ) {
      out[input.name] = input.default;
    }
  }
  return out;
}

/**
 * Presentational fields for a flow's inputs. Hidden inputs are skipped (their
 * defaults/presets resolve server-side). Reused by FlowInputsDialog and the
 * /tests ExecutionDialog Parameters section.
 */
export function FlowInputFields({
  inputs,
  values,
  errors,
  onChange,
}: {
  inputs: FlowInputDefinition[];
  values: Record<string, any>;
  errors?: Record<string, string>;
  onChange: (name: string, value: string, type: string) => void;
}) {
  const visible = inputs.filter((i) => !i.hidden);
  return (
    <>
      {visible.map((input) => (
        <div key={input.name} className="space-y-2">
          <Label htmlFor={input.name} className="text-sm font-medium text-foreground">
            {input.label || input.name}
            {input.required && <span className="text-destructive ml-1">*</span>}
          </Label>
          {input.description && (
            <p className="text-xs text-muted-foreground">{input.description}</p>
          )}
          {input.multiline && !isSecretField(input) ? (
            <Textarea
              id={input.name}
              rows={5}
              value={values[input.name] ?? ''}
              placeholder={input.default ? `Default: ${input.default}` : undefined}
              maxLength={input.maxLength}
              onChange={(e) => onChange(input.name, e.target.value, input.type)}
              className={errors?.[input.name] ? 'border-destructive' : ''}
            />
          ) : (
            <Input
              id={input.name}
              type={isSecretField(input) ? 'password' : input.type === 'number' ? 'number' : 'text'}
              value={values[input.name] ?? ''}
              placeholder={
                ESIM_RESOLVED_INPUTS.has(input.name)
                  ? 'Leave blank to use the eSIM’s APN'
                  : isPresetTemplate(input.default)
                    ? (isSecretField(input) ? 'Default: SimCheck preset (•••• )' : 'Default: SimCheck preset')
                    : input.default !== undefined
                      ? `Default: ${input.default}`
                      : undefined
              }
              maxLength={input.maxLength}
              onChange={(e) => onChange(input.name, e.target.value, input.type)}
              className={errors?.[input.name] ? 'border-destructive' : ''}
            />
          )}
          {ESIM_RESOLVED_INPUTS.has(input.name) && (
            <p className="text-xs text-muted-foreground">
              {(values[input.name] ?? '') !== ''
                ? 'Overrides the APN configured on the eSIM profile / group.'
                : 'Empty = use the APN configured on the eSIM profile / group / network (falls back to "internet").'}
            </p>
          )}
          {input.maxLength && (
            <p className="text-xs text-muted-foreground text-right">
              {(values[input.name] ?? '').length}/{input.maxLength}
            </p>
          )}
          {errors?.[input.name] && (
            <p className="text-xs text-destructive">{errors[input.name]}</p>
          )}
        </div>
      ))}
    </>
  );
}

export function FlowInputsDialog({
  isOpen,
  onClose,
  testLabel,
  inputs,
  onSubmit,
  storageKey,
}: FlowInputsDialogProps) {
  const [values, setValues] = useState<Record<string, any>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Initialize values: localStorage > defaults
  useEffect(() => {
    if (isOpen) {
      // Try to restore from localStorage
      let saved: Record<string, any> = {};
      if (storageKey) {
        try {
          const raw = localStorage.getItem(`${INPUTS_STORAGE_PREFIX}.${storageKey}`);
          if (raw) saved = JSON.parse(raw);
        } catch { /* ignore */ }
      }

      const initial: Record<string, any> = {};
      for (const input of inputs) {
        const savedVal = saved[input.name];
        // Skip restoring localStorage values that are preset templates —
        // an older build of this dialog used to round-trip the raw default
        // through localStorage at submit time, so existing users have
        // `${env.X}` cached as their "previous input." Treat those as
        // empty (= use server-side default) rather than pre-filling.
        if (savedVal !== undefined && savedVal !== '' && !isPresetTemplate(savedVal)) {
          initial[input.name] = savedVal;
        } else if (
          input.default !== undefined &&
          !isPresetTemplate(input.default) &&
          // eSIM-resolved inputs (apn) start empty — the placeholder explains
          // the inherited value; pre-filling the platform default would turn
          // every run into an explicit override (issue #161).
          !ESIM_RESOLVED_INPUTS.has(input.name)
        ) {
          // Plain default — pre-fill so the user can edit. Preset templates
          // (`${env.X}`) are intentionally NOT pre-filled: leave empty,
          // let the placeholder ("SimCheck preset") communicate the default,
          // and let the server fill in the actual value at runtime.
          initial[input.name] = input.default;
        } else {
          initial[input.name] = '';
        }
      }
      setValues(initial);
      setErrors({});
    }
  }, [isOpen, inputs, storageKey]);

  const handleChange = (name: string, value: string, type: string) => {
    setValues(prev => ({
      ...prev,
      [name]: type === 'number' ? (value === '' ? '' : Number(value)) : value,
    }));
    // Clear error on change
    if (errors[name]) {
      setErrors(prev => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const handleSubmit = () => {
    // Validate required fields
    const newErrors: Record<string, string> = {};
    for (const input of inputs) {
      if (input.required && (!values[input.name] || values[input.name] === '')) {
        newErrors[input.name] = `${input.name} is required`;
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Build final values (see finalizeFlowInputs): omit empty optionals, fall
    // back to plain defaults, and OMIT empty preset-template (`${env.X}`)
    // inputs so the server's ExecutionContext.resolveEnvDefault resolves them
    // (echoing the literal template back shipped the unresolved string to the
    // modem and caused real wire-level failures).
    const finalValues = finalizeFlowInputs(inputs, values);

    // Persist to localStorage for next time
    if (storageKey) {
      try {
        localStorage.setItem(`${INPUTS_STORAGE_PREFIX}.${storageKey}`, JSON.stringify(finalValues));
      } catch { /* ignore */ }
    }

    onSubmit(finalValues);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings2 className="w-5 h-5 text-primary" />
            Test Parameters
          </DialogTitle>
          <DialogDescription>
            Configure parameters for {testLabel}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto space-y-4 py-4 -mx-6 px-6">
          <FlowInputFields inputs={inputs} values={values} errors={errors} onChange={handleChange} />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit}>
            Continue
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
